import React, { useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

import TypeAvatar from './TypeAvatar';
import { getTypeProfilePath } from '../data/founderTypes';
import { buildTypeCode } from '../utils/founderIdentity';
import { formatFounderTitle } from '../utils/textUtils';
import { applyTypeTheme, clearTypeTheme } from '../utils/founderIdentity';
import { RESULTS } from '../data/productCopy';

const Hero = styled(motion.section)`
  width: 100%;
  text-align: center;
  margin-bottom: 2.5rem;
  padding: 2rem 1.25rem;
  border-radius: 20px;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.04) 0%,
    rgba(18, 18, 28, 0.5) 100%
  );
  border: 1px solid rgba(255, 255, 255, 0.08);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: var(--type-accent, var(--primary-color));
  }
`;

const IdentityCode = styled.div`
  font-size: 0.85rem;
  font-weight: 800;
  letter-spacing: 0.22em;
  color: var(--type-accent, var(--primary-color));
  margin-bottom: 0.75rem;
`;

const Headline = styled.h1`
  font-family: var(--font-display);
  font-size: clamp(1.75rem, 4.5vw, 2.35rem);
  font-weight: 800;
  color: var(--text-primary);
  margin: 0.75rem 0 0.5rem;
  letter-spacing: -0.03em;
  line-height: 1.15;
`;

const Tagline = styled.p`
  font-size: 1.1rem;
  color: var(--type-accent, var(--primary-light));
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const Subcopy = styled.p`
  font-size: 1rem;
  color: var(--text-secondary);
  line-height: 1.6;
  max-width: 540px;
  margin: 0 auto;
`;

const AvatarRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const TypeLink = styled(Link)`
  font-size: 0.9rem;
  color: var(--text-secondary);
  margin-top: 1rem;
  display: inline-block;

  &:hover {
    color: var(--type-accent, var(--primary-color));
  }
`;

const TypeIdentityHero = ({ profile, primaryType, secondaryType }) => {
  useEffect(() => {
    if (profile?.primaryType) applyTypeTheme(profile.primaryType);
    return () => clearTypeTheme();
  }, [profile?.primaryType]);

  if (!primaryType) return null;

  const typeCode = buildTypeCode(profile?.primaryType, profile?.secondaryType);
  const secondaryPct = profile?.secondaryType
    ? profile?.percentages?.[profile.secondaryType]
    : null;

  return (
    <Hero
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      aria-labelledby="identity-headline"
    >
      {typeCode && <IdentityCode>{typeCode}</IdentityCode>}
      <AvatarRow>
        <TypeAvatar type={primaryType} size={100} animate={false} />
        {secondaryType && <TypeAvatar type={secondaryType} size={72} animate={false} />}
      </AvatarRow>
      <Headline id="identity-headline">
        {formatFounderTitle(primaryType.title)}
        {secondaryType ? ` · ${formatFounderTitle(secondaryType.title)}` : ''}
      </Headline>
      <Tagline>{primaryType.tagline}</Tagline>
      <Subcopy>
        {profile?.percentages?.[profile.primaryType] || 0}% {primaryType.title.replace('The ', '')}
        {secondaryType && secondaryPct != null
          ? ` · ${secondaryPct}% ${secondaryType.title.replace('The ', '')}`
          : ''}
        . {RESULTS.heroSubcopy}
      </Subcopy>
      <TypeLink to={getTypeProfilePath(primaryType)}>Learn about {primaryType.title.replace('The ', 'the ')} →</TypeLink>
    </Hero>
  );
};

export default TypeIdentityHero;
